import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, WeakPasswordError } from '../contexts/KStackAuthContext';
import ThemeToggle from '../components/ThemeToggle';
import './Login.css';

interface ResetPasswordRouterState {
  email?: string;
  info?: string;
}

/**
 * Reset-password page (Phase F4, 2026-05-25).
 *
 * Posts to Nexus's POST /auth/reset-password with { email, code,
 * newPassword }. On success Nexus revokes all sessions, updates the
 * password hash, clears any lockout state, and returns 200 { ok: true }.
 *
 * Behaviors:
 *   - 200 ok           → /login w/ success info banner
 *   - 400 INVALID_CODE → "The reset code is invalid or has expired"
 *   - 400 WEAK_PASSWORD → render policy reasons inline (errors[])
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as ResetPasswordRouterState;

  const [email, setEmail] = useState(state.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [passwordReasons, setPasswordReasons] = useState<string[]>([]);
  const [info] = useState<string | null>(state.info || null);

  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg(null);
    setPasswordReasons([]);

    if (!email || code.length !== 6 || !newPassword) {
      setErrorMsg('Email, 6-digit code, and a new password are all required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(email, code, newPassword);
      navigate('/login', {
        state: {
          info: 'Password reset — sign in with your new password.',
        },
      });
    } catch (err) {
      if (err instanceof WeakPasswordError) {
        setPasswordReasons(err.reasons);
        setErrorMsg('New password does not meet policy:');
      } else {
        setErrorMsg(
          err instanceof Error ? err.message : 'Failed to reset password.',
        );
      }
    } finally {
      setTimeout(() => setIsLoading(false), 100);
    }
  };

  return (
    <div className="login-container">
      <ThemeToggle placement="corner" />
      <div className="login-box">
        <div className="login-header">
          <h1>Reset your password</h1>
        </div>
        <p>
          Enter the 6-digit code we emailed you, along with the new password
          you want to use.
        </p>

        {info && (
          <div className="banner-info">
            <p>{info}</p>
          </div>
        )}

        {errorMsg && (
          <div className="error-modal" onClick={() => setErrorMsg(null)}>
            <div className="error-content" onClick={(e) => e.stopPropagation()}>
              <div className="error-message">❌ {errorMsg}</div>
              {passwordReasons.length > 0 && (
                <ul style={{ textAlign: 'left', margin: '12px 0' }}>
                  {passwordReasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
              <button
                className="error-ok-button"
                onClick={() => setErrorMsg(null)}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Reset code</label>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={isLoading}
              placeholder="6-digit code"
              autoComplete="one-time-code"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '🔒'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              placeholder="Re-type your new password"
              autoComplete="new-password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="btn-primary"
          >
            {isLoading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <Link to="/login" className="btn-secondary-link">
          Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default ResetPassword;
